import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { FwTooltipDirective } from '@flywheel-io/vision';
import { parse } from 'date-fns';
import { catchError, Observable, Subscription, tap } from 'rxjs';

import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { CoreApiService } from 'app/src/core-api.service';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { ProjectsService } from 'app/src/projects/projects.service';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';

import { labelMaxLength } from '../../app.constants';
import { ChangeManagerService } from '../../change-manager.service';
import { ConfigService } from '../../config.service';
import { DialogService } from '../../dialogs';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { QueriesDialogComponent } from '../../queries/queries-dialog/queries-dialog.component';
import {
  SUBJECT_COHORT_OPTIONS,
  SUBJECT_ETHNICITY_OPTIONS,
  SUBJECT_MLSET_OPTIONS,
  SUBJECT_RACE_OPTIONS,
  SUBJECT_SEX_OPTIONS,
  SUBJECT_SPECIES_OPTIONS,
  SUBJECT_STATE_OPTIONS,
  SUBJECT_TYPE_OPTIONS,
} from '../../shared/constants/subject-info-options.constant';
import { formatDateStamp } from '../../shared/date.utils';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { Subject } from '../../shared/models/subject.model';
import { SubjectOption } from '../../shared/models/subject-option.model';
import { SessionService } from '../session.service';
@Component({
  host: { 'test-id': 'subject-info' },
  selector: 'subject-info',
  templateUrl: './subject-info.component.html',
  styleUrls: ['./subject-info.component.scss'],
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    DragDropModule,
    FileModule,
    FormsModule,
    FwTooltipDirective,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    SharedModule,
    RouterModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ProjectsService, CoreApiService],
})
export class SubjectInfoComponent implements OnChanges, OnDestroy {
  private changeManager = inject(ChangeManagerService);
  private config = inject(ConfigService);
  private dialog = inject(DialogService);
  private logger = inject(LoggingService);
  private notifications = inject(NotificationService);
  private sessionService = inject(SessionService);

  @Input() subject: Subject;
  @Input('permissions') permissionSet = new Set<PermissionAction>();

  @Output() afterSave = new EventEmitter<Subject>();

  metadata = new FormGroup({
    label: new FormControl('', [Validators.required, Validators.maxLength(labelMaxLength)]),
    type: new FormControl(''),
    firstname: new FormControl(''),
    lastname: new FormControl(''),
    dateOfBirth: new FormControl(null as Date | string),
    state: new FormControl(''),
    sex: new FormControl(''),
    cohort: new FormControl(''),
    mlset: new FormControl(''),
    race: new FormControl(''),
    ethnicity: new FormControl(''),
    species: new FormControl(''),
    strain: new FormControl(''),
  });

  options: Record<string, SubjectOption[]> = {};

  permissions = {
    editMetadata: false,
    deleteContainer: false,
    viewAuditTrail: false,
  };

  private subscriptions = {
    versionCheck: Subscription.EMPTY,
  };

  constructor() {
    this.options.type = [...SUBJECT_TYPE_OPTIONS];
    this.options.state = [...SUBJECT_STATE_OPTIONS];
    this.options.sex = [...SUBJECT_SEX_OPTIONS];
    this.options.cohort = [...SUBJECT_COHORT_OPTIONS];
    this.options.mlset = [...SUBJECT_MLSET_OPTIONS];
    this.options.race = [...SUBJECT_RACE_OPTIONS];
    this.options.ethnicity = [...SUBJECT_ETHNICITY_OPTIONS];
    this.options.species = [...SUBJECT_SPECIES_OPTIONS];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.permissionSet) {
      this.permissions = {
        editMetadata: this.permissionSet.has(PermissionAction.ContainersModifyMetadata),
        deleteContainer: this.permissionSet.has(PermissionAction.ContainersDeleteHierarchy),
        viewAuditTrail:
          this.permissionSet.has(PermissionAction.ContainersViewMetadata) &&
          this.config.isFeatureEnabled('audit_trail'),
      };
      if (this.permissions.editMetadata) {
        this.changeManager.registerForm('SubjectInfoComponent', {
          control: this.metadata,
          reset: () => this.resetForm(),
          save: () => this._saveChanges(),
          successMessageKey: 'session.saveSubject.success',
          errorMessageKey: 'session.saveSubject.error',
        });
      }
    }
    if (
      changes.subject &&
      this.subject?._id &&
      (this.metadata.pristine ||
        changes.subject.firstChange ||
        this.subject?._id !== changes.subject.previousValue?._id)
    ) {
      this.resetForm();
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
    this.changeManager.deregisterForm('SubjectInfoComponent');
  }

  get type(): string {
    return this.metadata.get('type').value;
  }

  resetForm(): void {
    // add options for unknown values
    for (const [field, options] of Object.entries(this.options)) {
      if (this.subject[field] && !options.some((option) => option.value === this.subject[field])) {
        options.push({ value: this.subject[field] });
      }
    }

    const parsedDob =
      typeof this.subject.date_of_birth === 'string'
        ? parse(this.subject.date_of_birth, 'yyyy-MM-dd', new Date())
        : null;

    this.metadata.reset({
      label: this.subject.label || '',
      type: this.subject.type || '',
      firstname: this.subject.firstname || '',
      lastname: this.subject.lastname || '',
      dateOfBirth: parsedDob || '',
      state: this.subject.state || '',
      sex: this.subject.sex || '',
      cohort: this.subject.cohort || '',
      mlset: this.subject.mlset || '', // version >= 15.6.0
      race: this.subject.race || '',
      ethnicity: this.subject.ethnicity || '',
      species: this.subject.species || '',
      strain: this.subject.strain || '',
    });
    this.metadata.markAsPristine();
    this.metadata.updateValueAndValidity();
  }

  private _saveChanges(): Observable<void> {
    const form = this.metadata.value;
    // The API takes a date, not a timestamp: a Date from the picker would serialise as a full
    // ISO string and be rejected. Formatted from the local parts so the day cannot shift.
    const dateOfBirth = form.dateOfBirth ? formatDateStamp(form.dateOfBirth) : null;
    const payload: Partial<Subject> = {
      label: form.label,
      type: form.type || null,
    };
    // clear out any previously-set fields
    for (const field of ['firstname', 'lastname', 'species', 'strain']) {
      // some fields must be strings
      if (this.subject[field]) {
        payload[field] = '';
      }
    }
    for (const field of ['date_of_birth', 'state', 'sex', 'cohort', 'race', 'ethnicity']) {
      // some fields must be null
      if (this.subject[field]) {
        payload[field] = null;
      }
    }
    if (this.type === 'human' || !this.type) {
      Object.assign(payload, {
        firstname: form.firstname || '',
        lastname: form.lastname || '',
        date_of_birth: dateOfBirth,
        state: form.state || null,
        sex: form.sex || null,
        cohort: form.cohort || null,
        race: form.race || null,
        ethnicity: form.ethnicity || null,
      });
    }
    if (this.type === 'animal' || !this.type) {
      Object.assign(payload, {
        date_of_birth: dateOfBirth,
        state: form.state || null,
        sex: form.sex || null,
        cohort: form.cohort || null,
        species: form.species || '',
        strain: form.strain || '',
      });
    }
    if (['human', 'animal'].includes(this.type) || !this.type) {
      Object.assign(payload, { mlset: form.mlset || null });
    }
    return this.sessionService.saveSubject(this.subject._id, payload).pipe(
      tap(() => {
        this.metadata.markAsPristine();
        this.afterSave.emit({ ...this.subject, ...payload });
      }),
      catchError((error) => {
        if (error.status === 422) {
          this.logger.warn(error);
          this.notifications.error('session.saveSubject.errorDuplicateCode');
        } else {
          this.logger.error(error);
        }
        throw error;
      }),
    );
  }

  saveChanges(): void {
    this._saveChanges().subscribe({
      next: () => {
        this.notifications.success('session.saveSubject.success');
      },
      error: (error) => {
        if (error.status !== 422) {
          this.notifications.error('session.saveSubject.error');
        }
      },
    });
  }

  deleteSubject(): void {
    this.sessionService.deleteSubject(this.subject).subscribe();
  }

  viewAuditTrail(): void {
    this.dialog.open(QueriesDialogComponent, {
      hasBackdrop: true,
      disableClose: false,
      height: '100%',
      width: '850px',
      panelClass: ['border-radius-0', 'padding-0'],
      position: { right: '0' },
      data: {
        auditable: this.subject,
        type: 'object',
      },
    });
  }
}
